'use client';

import { InlineAuth } from '@/components/auth/InlineAuth';
import { BAR_CAPSULE, segmentPill } from '@/components/ui/bar';
import { useBarPanel } from './use-bar-panel';

/**
 * The pill BECOMES the form. One action per state: a sheet with its own Send
 * code, floating beside the Sign in pill that opened it, is two calls to action
 * competing in one bar.
 *
 * The form itself is InlineAuth — the same component and the same two steps the
 * product cards use, given a prompt and a cta like every other caller. This file
 * decides only where it sits, and how to put it away: a click outside or Escape
 * returns the pill. Never a modal; nothing behind it is blocked or dimmed.
 */
export function SignInPill() {
  // No `onEscape` here: the button that opened this is unmounted while the form
  // stands in its place, so there is nothing left to hand focus back to.
  const { open, setOpen, wrap } = useBarPanel();

  if (!open) {
    return (
      // The capsule and its segments — the same two parts every other control
      // in the bar is built from, so this is level with them and answers the
      // pointer the same way. The fill keeps Sign in the one thing to press.
      <div className={BAR_CAPSULE}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={false}
          className={segmentPill(true)}
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div
      ref={wrap}
      className="mm-unfold pointer-events-auto w-[19rem] origin-top-right rounded-[22px] bg-[var(--bar-raise)] p-4 shadow-[shadow:var(--bar-shadow)] lg:origin-top-right"
    >
      <InlineAuth
        id="site-bar"
        source="navbar"
        tone="card"
        prompt="Sign in to see your calls and your page"
        cta="Send code"
        onSuccess={() => {
          // Put the form away and nothing else. No refresh: the cookie write
          // already re-rendered the whole tree, so every surface — the main
          // page's own list included — shows them signed in by itself. Signing
          // in never moves anybody any more (2026-08-31); the bar is one tap
          // from wherever they want to go.
          setOpen(false);
        }}
      />
    </div>
  );
}
